import React, { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Link from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AccessTimeRoundedIcon from '@material-ui/icons/AccessTimeRounded';
import EventRoundedIcon from '@material-ui/icons/EventRounded';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import ConditionalActionWrapper from 'UI/components/atoms/ConditionalWrapper';
import FPAvatar from 'UI/components/atoms/FPAvatar';
import FPChip from 'UI/components/atoms/FPChip';
import { When } from 'UI/components/atoms/When';
import { VerdictConfig } from 'UI/components/molecules/EmailWithValidation';
import InfoLabel, { InfoLabelModes } from 'UI/components/molecules/InfoLabel';
import { EMAIL_VALIDATION_STATUSES } from 'UI/constants/defaults';
import { getInitials } from 'UI/utils/getinitials';

import { ContactInfoSection } from './ContactInfoSection';
import { ExpandableList } from './ExpandableList';
import {
  ASSOCIATIONS_DISPLAY_LIMIT,
  CERTIFICATIONS_DISPLAY_THRESHOLD,
  EXPERIENCE_DISPLAY_THRESHOLD,
  SKILLS_DISPLAY_THRESHOLD
} from './ResumeCard.constants';
import { useResumeCardStyles } from './ResumeCard.styled';
import {
  extractResumeData,
  formatCreatedAtLine,
  formatDescription,
  formatEducationPeriod,
  formatLastActivityLine,
  formatPhoneDisplay,
  formatTier1Line,
  formatWorkPreferencesLine,
  getExpandButtonText,
  getPeriodString
} from './ResumeCard.utils';
import { ResumeCardActions } from './ResumeCardActions';
import { ResumeSection } from './ResumeSection';

export const ResumeCard = React.memo(
  ({
    resume,
    candidateTypeColor,
    candidateStatusColor,
    isSelected = false,
    onSelectionChange,
    onViewDetails
  }) => {
    const classes = useResumeCardStyles();
    const [isSkillsExpanded, setIsSkillsExpanded] = React.useState(false);

    const {
      associations,
      candidateStatus,
      candidateType,
      certifications,
      createdAt,
      currentCompany,
      educations,
      emails,
      emailStatus,
      experiences,
      fullName,
      functionalTitle,
      idealTimeToStart,
      industry,
      lastActivityDate,
      lastActivityDetail,
      location,
      phoneDetails,
      phoneNumbers,
      profession,
      skills,
      specialty,
      subSpecialty,
      teamName,
      urls,
      workPreferences,
      yearsExperience
    } = extractResumeData(resume);

    const tier1Line = formatTier1Line({
      profession,
      functionalTitle,
      currentCompany,
      yearsExperience
    });

    const lastActivityLine = formatLastActivityLine(lastActivityDetail);
    const createdAtLine = formatCreatedAtLine(createdAt);
    const workPreferencesLine = formatWorkPreferencesLine(workPreferences, idealTimeToStart);
    const { recruiter } = lastActivityDetail || {};

    const metadata = {
      typeLabel: candidateType?.title,
      statusLabel: candidateStatus?.title,
      createdAt,
      lastActivityDate
    };
    const hasTypeChip = Boolean(metadata.typeLabel);
    const hasStatusChip = Boolean(metadata.statusLabel);
    const candidateId = resume?.candidate?.id;
    const shouldRenderChipRow = hasTypeChip || hasStatusChip || Boolean(VerdictConfig[emailStatus]);
    const shouldRenderSpecialtyInfo = Boolean(specialty || subSpecialty || industry);

    const renderExperienceItem = useCallback(
      (exp, index) => (
        <Box key={`${exp.company}-${exp.title}-${index}`} mb={0.5}>
          {exp.title && (
            <Typography component="span">
              {exp.title}
              {(exp.company || exp.location) && <> •&nbsp;</>}
            </Typography>
          )}
          <Typography variant="body2" component="span">
            {exp.company}
            {exp.location && `${exp.company ? ' • ' : ''}${exp.location}`}
          </Typography>
          {(exp.start_date || exp.end_date) && (
            <Typography variant="body2" component="span">
              &nbsp;({getPeriodString(exp.start_date, exp.end_date, exp.is_current)})
            </Typography>
          )}
        </Box>
      ),
      []
    );

    const renderEducationItem = useCallback(
      (edu, index) => (
        <Box key={`${edu.issuing_organization}-${edu.end_year}-${index}`} mb={0.5}>
          <Typography component="span">
            {edu.issuing_organization}
            {edu.degree && <> •&nbsp;</>}
          </Typography>
          {edu.degree && (
            <Typography variant="body2" component="span">
              {edu.degree}
            </Typography>
          )}
          {(edu.start_year || edu.end_year) && (
            <Typography variant="body2" component="span">
              &nbsp;({formatEducationPeriod(edu.start_year, edu.end_year)})
            </Typography>
          )}
          {edu.description && (
            <Typography variant="body2" display="block" className={classes.descriptionText}>
              {formatDescription(edu.description)}
            </Typography>
          )}
        </Box>
      ),
      [classes.descriptionText]
    );

    const renderCertificationItem = useCallback(
      (cert, index) => (
        <Box key={`${cert.issuing_organization}-${index}`} mb={0.5}>
          {cert.description && (
            <Typography component="span" className={classes.descriptionText}>
              {formatDescription(cert.description)}
            </Typography>
          )}
          {cert.issuing_organization && (
            <Typography variant="body2" component="span">
              {cert.description && <>&nbsp;• </>}
              {cert.issuing_organization}
            </Typography>
          )}
          {cert.issued_date?.year && (
            <Typography variant="body2" component="span">
              &nbsp;({cert.issued_date.year})
            </Typography>
          )}
        </Box>
      ),
      [classes.descriptionText]
    );

    const renderAssociationItem = useCallback(
      (assoc, index) => (
        <Box key={`${assoc.issuing_organization}-${assoc.role}-${index}`} mb={0.5}>
          <Typography component="span">
            {assoc.issuing_organization}
            {assoc.role && <> •&nbsp;</>}
          </Typography>
          {assoc.role && (
            <Typography variant="body2" component="span">
              {assoc.role}
            </Typography>
          )}
          {(assoc.start_date || assoc.end_date) && (
            <Typography variant="body2" component="span">
              &nbsp;({getPeriodString(assoc.start_date, assoc.end_date)})
            </Typography>
          )}
        </Box>
      ),
      []
    );

    const expandExperienceText = useMemo(
      () => getExpandButtonText({ items: experiences }),
      [experiences]
    );
    const expandSkillsText = useMemo(() => getExpandButtonText({ items: skills }), [skills]);
    const expandCertificationsText = useMemo(
      () => getExpandButtonText({ items: certifications }),
      [certifications]
    );
    const associationOverflowText = useMemo(
      () => getExpandButtonText({ items: associations }),
      [associations]
    );

    const displayedSkills = useMemo(
      () => (isSkillsExpanded ? skills : skills.slice(0, SKILLS_DISPLAY_THRESHOLD)),
      [isSkillsExpanded, skills]
    );

    const handleCheckboxChange = useCallback(() => {
      if (onSelectionChange) {
        onSelectionChange(resume.candidate?.id);
      }
    }, [onSelectionChange, resume]);

    return (
      <li className={classes.resumeItem}>
        <Box className={classes.actionsContainer}>
          <ResumeCardActions
            candidateId={resume.candidate?.id}
            phone={formatPhoneDisplay(phoneNumbers[0]) || ''}
            email={emails[0] || ''}
            onViewDetails={onViewDetails}
          />
        </Box>
        <Box className={classes.checkboxContainer}>
          <Checkbox
            checked={isSelected}
            color="primary"
            data-testid={`resume-card-checkbox-${resume.candidate?.id}`}
            inputProps={{ 'aria-label': `Select ${fullName}` }}
            onChange={handleCheckboxChange}
          />
        </Box>
        <Box className={classes.resumeContent}>
          <Box className={classes.resumeHeader}>
            <Box className={classes.resumeInfoBox}>
              <Typography variant="body1" display="inline">
                {candidateId ? (
                  <Link
                    component={RouterLink}
                    to={EntityRoutes.CandidateProfile.replace(':id', candidateId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {fullName}
                  </Link>
                ) : (
                  fullName
                )}
                {location && (
                  <Typography component="span" color="textSecondary">
                    &nbsp;• {location}
                  </Typography>
                )}
              </Typography>
              {shouldRenderChipRow && (
                <div className={classes.metadataRow} data-testid="resume-card-metadata-row">
                  {hasTypeChip && (
                    <FPChip
                      data-testid="resume-card-type-chip"
                      label={candidateType.title}
                      color={candidateTypeColor}
                      size="small"
                      textColor={candidateTypeColor ? 'white' : 'inherit'}
                    />
                  )}
                  {hasStatusChip && (
                    <FPChip
                      data-testid="resume-card-status-chip"
                      label={candidateStatus.title}
                      color={candidateStatusColor}
                      size="small"
                      textColor="inherit"
                      variant="outlined"
                    />
                  )}
                  <When condition={VerdictConfig[emailStatus]}>
                    <ConditionalActionWrapper
                      condition={VerdictConfig[emailStatus]?.info}
                      wrapper={children => (
                        <Tooltip title={VerdictConfig[emailStatus]?.info} placement="bottom" arrow>
                          {children}
                        </Tooltip>
                      )}
                    >
                      <Chip
                        aria-label={
                          strings.inventoryProfiles.common.emailValidationStatus[emailStatus]
                        }
                        className={
                          emailStatus === EMAIL_VALIDATION_STATUSES.RISKY
                            ? classes.emailWarningChip
                            : undefined
                        }
                        data-testid={`resume-card-email-status-${emailStatus}`}
                        icon={VerdictConfig[emailStatus]?.icon}
                        label={strings.resumeSearch.resumeCard.emailLabel}
                        size="small"
                        variant="outlined"
                      />
                    </ConditionalActionWrapper>
                  </When>
                </div>
              )}
            </Box>
            <When condition={tier1Line}>
              <Typography variant="body1" className={classes.titleLine}>
                {tier1Line}
              </Typography>
            </When>
            {shouldRenderSpecialtyInfo && (
              <Typography variant="body2" color="textSecondary">
                {[industry, specialty, subSpecialty].filter(Boolean).join(' › ')}
              </Typography>
            )}
            <When condition={workPreferencesLine}>
              <Typography variant="body2" className={classes.keyFactsLine}>
                {workPreferencesLine}
              </Typography>
            </When>
            <When condition={createdAtLine || recruiter?.fullName || lastActivityLine}>
              <Box className={classes.activityFooter} data-testid="resume-card-activity-footer">
                <When condition={createdAtLine}>
                  <Box className={classes.activityFooterItem}>
                    <EventRoundedIcon style={{ width: 16, height: 16 }} color="action" />
                    <Typography variant="body2" color="textSecondary">
                      {strings.formatString(strings.resumeSearch.resumeCard.metadata.createdAt, {
                        date: createdAtLine
                      })}
                    </Typography>
                  </Box>
                </When>
                <When condition={recruiter?.fullName}>
                  <Box className={classes.activityFooterItem}>
                    <FPAvatar
                      mode="user"
                      size="small"
                      style={{ width: 20, height: 20, fontSize: 9 }}
                      userEmail={String(recruiter?.id)}
                    >
                      {getInitials(recruiter?.fullName || '')}
                    </FPAvatar>
                    <Typography variant="body2" color="textSecondary">
                      {strings.formatString(strings.resumeSearch.resumeCard.metadata.recruiter, {
                        recruiter: recruiter?.fullName
                      })}
                      <When condition={teamName}>
                        <Typography component="span" variant="body2" color="textSecondary">
                          {strings.resumeSearch.resumeCard.teamSeparator}
                          {teamName}
                        </Typography>
                      </When>
                    </Typography>
                  </Box>
                </When>
                <When condition={lastActivityLine}>
                  <Box className={classes.activityFooterItem}>
                    <AccessTimeRoundedIcon style={{ width: 16, height: 16 }} color="action" />
                    <Typography variant="body2" color="textSecondary">
                      {strings.formatString(strings.resumeSearch.resumeCard.metadata.lastActivity, {
                        activity: lastActivityLine
                      })}
                    </Typography>
                  </Box>
                </When>
              </Box>
            </When>
          </Box>

          <ContactInfoSection
            candidateId={resume.candidate?.id}
            phoneDetails={phoneDetails}
            emails={emails}
            urls={urls}
          />

          <ResumeSection
            isEmpty={experiences.length === 0}
            title={strings.resumeSearch.resumeCard.sections.experience}
          >
            <ExpandableList
              items={experiences}
              displayThreshold={EXPERIENCE_DISPLAY_THRESHOLD}
              renderItem={renderExperienceItem}
              expandButtonText={expandExperienceText}
              collapseButtonText={strings.resumeSearch.resumeCard.showFewer}
            />
          </ResumeSection>

          <ResumeSection
            isEmpty={skills.length === 0}
            title={strings.resumeSearch.resumeCard.sections.skills}
          >
            <>
              <InfoLabel description={displayedSkills} mode={InfoLabelModes.CHIP} />
              {skills.length > SKILLS_DISPLAY_THRESHOLD && (
                <Button
                  variant="text"
                  size="small"
                  color="secondary"
                  disableRipple
                  onClick={() => setIsSkillsExpanded(!isSkillsExpanded)}
                >
                  {isSkillsExpanded ? strings.resumeSearch.resumeCard.showFewer : expandSkillsText}
                </Button>
              )}
            </>
          </ResumeSection>

          <ResumeSection
            isEmpty={educations.length === 0}
            title={strings.resumeSearch.resumeCard.sections.education}
          >
            {educations.map(renderEducationItem)}
          </ResumeSection>

          <ResumeSection
            isEmpty={certifications.length === 0}
            title={strings.resumeSearch.resumeCard.sections.certifications}
          >
            <ExpandableList
              items={certifications}
              displayThreshold={CERTIFICATIONS_DISPLAY_THRESHOLD}
              renderItem={renderCertificationItem}
              expandButtonText={expandCertificationsText}
              collapseButtonText={strings.resumeSearch.resumeCard.showFewer}
            />
          </ResumeSection>

          <ResumeSection
            isEmpty={associations.length === 0}
            title={strings.resumeSearch.resumeCard.sections.associations}
          >
            <ExpandableList
              items={associations}
              displayThreshold={ASSOCIATIONS_DISPLAY_LIMIT}
              renderItem={renderAssociationItem}
              expandButtonText={associationOverflowText}
              collapseButtonText={strings.resumeSearch.resumeCard.showFewer}
            />
          </ResumeSection>
        </Box>
      </li>
    );
  }
);
